<script lang="ts">
  import { orderBy } from "lodash";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { get } from "svelte/store";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import SalesRepSection from "@retailer/svelte/Shared/SalesRepSection.svelte";
  import ExpandableList from "@retailer/svelte/ProductDetails/Components/ExpandableList.svelte";
  import { brandFamilyPageParamsFields } from "@retailer/services/brand_family_page_params";
  import type { SalesRep } from "@shared/types/SalesRep";
  import type { ProductLine, Product } from "@shared/types/ProductLine";
  import type { Distributor } from "@shared/types/Distributor";
  import DistributorLink from "./DistributorLink.svelte";
  import { sortedDistributors } from "@retailer/services/distributor_service";
  import type { RetailerDistributor } from "@shared/types/RetailerDistributor";
  import type { SalesDivision } from "@shared/types/SevenFifty";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { getStockSiteLocation } from "@shared/services/retailer_user_authentication_service";
  import { OHLQ_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";
  import { VT_802_SPIRITS_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  export let accountDataLoaded: boolean = false;
  export let mobileMode: boolean;
  export let productLine: ProductLine;
  export let hasDiscount: boolean;
  export let selectedVintageProduct: Product;
  export let salesReps: SalesRep[];
  export let distributors: Distributor[];
  export let distributor: Distributor;
  export let retailerDistributor: RetailerDistributor;
  export let showDualProductExperience: boolean;
  export let openConnectDistributorModal: () => void;
  export let producerName: string;
  export let parentProducerName: string;
  export let salesDivisions: SalesDivision[] = [];
  export let hideAllDistributorInfo: boolean = false;

  let distributorSelection: Distributor[] = [];

  let wineAppellation;
  let wineCountry;
  let wineRegion;
  let showDetails;
  let showSoldBy: boolean = false;
  $: {
    wineAppellation = selectedVintageProduct?.wine_appellation || productLine.wine_appellation;
    wineCountry = selectedVintageProduct?.wine_country || productLine.wine_country;
    wineRegion = selectedVintageProduct?.wine_region || productLine.wine_region;

    showDetails =
      wineAppellation ||
      selectedVintageProduct?.wine_varietal ||
      productLine.wine_varietal ||
      selectedVintageProduct?.abv ||
      productLine.abv ||
      wineRegion ||
      wineCountry;
  }
  const showAccountNumberEntry = stagedRolloutCheck(
    "connect_distributor_modal_manual_account_number",
  );
  let needsAccountNumber: boolean;
  let accountNumberText: string;
  let ctaText: string;
  let showSalesReps: boolean;
  let showAccountNumberButton: boolean;

  $: salesRepsNeeded = distributor && !distributor.no_sales_reps_needed;
  $: {
    needsAccountNumber =
      showAccountNumberEntry &&
      distributor?.requires_account_number &&
      !distributor?.is_rolling_out_integrated_orders &&
      !retailerDistributor?.account_number;

    accountNumberText =
      distributor?.id === VT_802_SPIRITS_DISTRIBUTOR_ID ? "Connect Account" : "Add account number";
    ctaText = needsAccountNumber ? accountNumberText : "Add sales rep";

    showSalesReps = !needsAccountNumber;
    showAccountNumberButton =
      accountDataLoaded && (needsAccountNumber || (salesRepsNeeded && !salesReps.length));
  }

  $: {
    showSoldBy = !!(distributor || {}).name || showDualProductExperience;
    if (showDualProductExperience && distributors.length) {
      distributorSelection = sortedDistributors(distributors);
    }
  }

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  let retailerAuthApplicable: boolean;

  $: retailerAuthApplicable =
    hasRetailerUserAuthRollout &&
    get(DistributorsStore).find(d => d.id == distributor?.id)?.require_retailer_user_authentication;

  $: retailerAuth = $RetailerUserAuthenticationsStore.find(
    a => a.distributor_id == distributor?.id,
  );
</script>

<style>
  div + div.divider {
    height: 15px;
    border-left: 1px solid #bfbfbf;
    padding-left: 0.5rem !important;
  }
  .center-image-mobile {
    margin: auto !important;
  }

  .fizz-flex-wrap {
    gap: 0.5rem;
  }
</style>

<div
  class="media fizz-surface fizz-flex-row-24"
  class:fizz-stack-24={!mobileMode}
  class:fizz-stack-16={mobileMode}
  data-testid="product-line-box"
>
  <div id="product-image" class="fizz-inset-square-24" class:center-image-mobile={mobileMode}>
    <ProductImage
      width={150}
      height={200}
      product={selectedVintageProduct && selectedVintageProduct.image_upload_id
        ? selectedVintageProduct
        : productLine}
      fizz={true}
      className={"fizz-full-width"}
      usePlaceholderImage={true}
    />
  </div>

  <div style="margin-block-end: auto">
    {#if hasDiscount}
      <div
        class="fizz-badge-icon"
        class:fizz-stack-24={!mobileMode}
        class:fizz-stack-8={mobileMode}
      >
        <Icon className="fizz-icon-24" type="DealCircled" height="24" width="24" />
        <span class="fizz-text-medium fizz-text-strong">DEAL</span>
      </div>
    {/if}

    <h1 class="fizz-heading-1 notranslate fizz-stack-8">
      {productLine.name}
    </h1>

    {#if showDetails}
      <div class="fizz-stack-8 fizz-text-medium">
        <div class="fizz-stack-8" class:fizz-flex-row-inline-8={!mobileMode}>
          {#if wineAppellation}
            <div class="fizz-flex-row-inline-4" class:fizz-stack-8={mobileMode}>
              <div class="fizz-text-strong">Appellation:</div>
              <div style="white-space: nowrap;">
                <UIRouterLink
                  stateName={"layout.product_listing"}
                  params={{ appellation: wineAppellation.id }}
                  classes="fizz-text-strong cursor-pointer"
                >
                  {wineAppellation.name}
                </UIRouterLink>
              </div>
            </div>
          {/if}
        </div>

        <div class="fizz-flex-row-8 fizz-flex-wrap">
          {#if wineRegion}
            <div class="fizz-flex-row-inline-4">
              <div class="fizz-text-strong">Region:</div>
              <div>
                <UIRouterLink
                  stateName={"layout.product_listing"}
                  params={{ region: wineRegion.id }}
                  classes="fizz-text-strong cursor-pointer"
                >
                  {wineRegion.name}
                </UIRouterLink>
              </div>
            </div>
          {/if}

          {#if wineCountry}
            <div class="fizz-flex-row-inline-4">
              <div class="fizz-text-strong">Country:</div>
              <div>
                <UIRouterLink
                  stateName={"layout.product_listing"}
                  params={{ country: wineCountry.id }}
                  classes="fizz-text-strong cursor-pointer"
                >
                  {wineCountry.name}
                </UIRouterLink>
              </div>
            </div>
          {/if}

          {#if selectedVintageProduct?.abv || productLine.abv}
            <div class="fizz-flex-row-inline-4 divider" style="height: 100%">
              <div class="fizz-align-self-center">
                <span class="fizz-text-strong">ABV:</span>
                <span>{selectedVintageProduct?.abv || productLine.abv}%</span>
              </div>
            </div>
          {/if}
        </div>
      </div>
    {/if}

    <ExpandableList
      displayLabel="Feature"
      list={orderBy(productLine.tags, "num_product_lines", "desc")}
      linkBase="/product_listing?tagId="
    />

    <ExpandableList
      displayLabel="Raw Materials"
      list={orderBy(productLine.wine_varietals, "display_order")}
      linkBase="/product_listing?rawMaterial="
    />

    <div class="fizz-text-medium">
      {#if producerName}
        <div class="fizz-flex-row-inline-4 fizz-flex-wrap fizz-stack-8">
          <p>
            <span class="fizz-text-strong">Producer:</span>
            {producerName}
            {#if parentProducerName}
              {` - ${parentProducerName}`}
            {/if}
          </p>
        </div>
      {/if}
      {#if productLine.brand_family.name}
        <div class="fizz-stack-8">
          <UIRouterLink
            stateName={"layout.product_listing"}
            params={brandFamilyPageParamsFields(
              productLine.brand_family.id,
              productLine.brand_family.name,
            )}
            classes="fizz-text-strong cursor-pointer brand-family-search-link"
          >
            View All <span class="notranslate">{productLine.brand_family.name}</span>
          </UIRouterLink>
        </div>
      {/if}
      {#if showSoldBy && !hideAllDistributorInfo}
        <div class="fizz-stack-8">
          <span class="product-line-detail-distributor-name fizz-text-strong fizz-flex-row-4">
            {#if showDualProductExperience}
              <span class="fizz-flex-row-inline-4 fizz-flex-wrap" style="--gap: 2px">
                <span>Sold by:</span>
                {#each distributorSelection as dual_product_distributor, index}
                  <DistributorLink
                    distributor={dual_product_distributor}
                    addComma={index < distributors.length - 1}
                  />
                {/each}
              </span>
            {:else}
              <span>Sold by:</span>
              <DistributorLink {distributor} />
            {/if}
          </span>
        </div>
      {/if}

      {#if !(distributor || {}).seven_fifty_integration && productLine.brand_family.name !== productLine.manufacturer_name && productLine.manufacturer_name}
        <div class="fizz-stack-8">
          <span class=" fizz-text-strong"
            >Supplied by:
            <UIRouterLink
              stateName={"layout.product_listing"}
              params={{
                manufacturerId: productLine.manufacturer_id,
                manufacturerName: productLine.manufacturer_name,
              }}
              classes="fizz-text-strong cursor-pointer manufacturer-search-link"
            >
              <span class="notranslate">{productLine.manufacturer_name}</span>
            </UIRouterLink>
          </span>
        </div>
      {/if}

      {#if !retailerAuthApplicable}
        {#if salesDivisions && salesDivisions.length > 0}
          <div class="fizz-flex-row-inline-4 fizz-flex-wrap fizz-stack-8">
            <p>
              <span class="fizz-text-strong"
                >Sales {pluralize("Division", salesDivisions.length)}:</span
              >
              {salesDivisions.map(sd => sd.name).join(", ")}
            </p>
          </div>
        {/if}

        <div>
          {#if showSalesReps}
            <SalesRepSection bind:salesReps {distributor} />
          {/if}

          {#if showAccountNumberButton}
            <button class="fizz-button fizz-button-small" on:click={openConnectDistributorModal}>
              {ctaText}
            </button>
          {/if}
        </div>
      {:else if !hideAllDistributorInfo}
        {#if distributor?.id === OHLQ_DISTRIBUTOR_ID}
          <div class="fizz-flex-row-inline-4 fizz-flex-wrap fizz-stack-8">
            <p>
              <span class="fizz-text-strong">My Agency:</span>
              {#await getStockSiteLocation(retailerAuth?.distributor_id, retailerAuth?.account_number) then data}
                <UIRouterLink stateName={"layout.accounts"}>
                  <strong>
                    {data.name || ""}
                  </strong>
                </UIRouterLink>
              {/await}
            </p>
          </div>
        {:else}
          {#if showSalesReps}
            <SalesRepSection bind:salesReps {distributor} />
          {/if}

          {#if showAccountNumberButton}
            <button class="fizz-button fizz-button-small" on:click={openConnectDistributorModal}>
              Connect Account
            </button>
          {/if}
        {/if}
      {/if}
    </div>
  </div>
</div>
